import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { addDays, format, startOfDay } from 'date-fns';

import { formatFEIN } from 'feature/loading-management/utils/format';
import MDropdownMenuButton from 'common/MDropdownMenuButton';
import { createSnack } from 'snackbar/snackbarSlice';
import { useDispatch } from 'react-redux';
import { BulkActionFooter, PersistentServerSideDataGrid, useCustomizablePopover } from 'common';
import { useUserRolePermissions } from 'hooks';
import { formatNumber } from 'utils/helpers/format';
import { AuthContext } from 'authentication/AuthContext';
import usePersistentState from 'hooks/usePersistentState';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';
import {
  useGetCarrierAuthorizationsListQuery,
  useLazyExportCarrierAuthorizationsQuery,
  useRemoveBulkCarrierAuthorizationsMutation,
  useRemoveCarrierAuthorizationMutation,
} from './carrierAuthorizationsEndpoints';

const SORT_COLUMN_MAP = {
  carrierName: 'Carrier',
  fein: 'Fein',
  insuranceExpiration: 'InsuranceExpiration',
  location: 'Location',
  scac: 'Scac',
};

const MAX_VISIBLE_CUSTOMERS = 10;

const columns = (
  onEdit,
  onRemove,
  isProductLoadingManage,
  { handlePopoverOpen, handlePopoverClose, setActiveRowId }
) => [
  { field: 'carrierName', flex: 1.3, headerName: 'Carrier', minWidth: 220 },
  { field: 'scac', flex: 0.8, headerName: 'SCAC', minWidth: 120 },
  {
    field: 'fein',
    flex: 1,
    headerName: 'FEIN',
    minWidth: 140,
    renderCell: params => {
      const feinDigits = `${params.value || ''}`.replace(/\D/g, '');
      return feinDigits.length === 9 ? formatFEIN(feinDigits.split('')) : '--';
    },
  },
  { field: 'location', flex: 1.4, headerName: 'Location', minWidth: 220 },
  {
    field: 'insuranceExpiration',
    flex: 1.1,
    headerName: 'Insurance Expiration',
    minWidth: 190,
    renderCell: params => {
      if (!params.value) return '--';

      const insuranceDate = startOfDay(new Date(params.value));
      const currentDate = startOfDay(new Date());
      const sixtyDaysFromNow = addDays(currentDate, 60);

      const isExpired = insuranceDate < currentDate;
      const isMoreThanSixtyDays = insuranceDate > sixtyDaysFromNow;

      let tooltipMessage = 'Expiring within the next 60 days';
      let badgeColor = {
        backgroundColor: '_next.statusChip.warning.background',
        color: '_next.statusChip.warning.dark',
      };

      if (isExpired) {
        tooltipMessage = 'Record is expired';
        badgeColor = {
          backgroundColor: '_next.statusChip.error.background',
          color: '_next.statusChip.error.main',
        };
      } else if (isMoreThanSixtyDays) {
        tooltipMessage = 'More than 60 days until expiration';
        badgeColor = {
          backgroundColor: '_next.statusChip.primary.background',
          color: '_next.statusChip.primary.dark',
        };
      }

      return (
        <Tooltip placement="top" title={tooltipMessage}>
          <Chip
            label={format(insuranceDate, 'MM/dd/yyyy')}
            size="small"
            sx={{
              backgroundColor: badgeColor.backgroundColor,
              color: badgeColor.color,
              fontFamily: 'Overpass',
              fontSize: '0.8125rem',
              fontWeight: 400,
            }}
          />
        </Tooltip>
      );
    },
  },
  {
    aggregable: true,
    field: 'customersRestricted',
    flex: 1,
    headerName: 'Customers Restricted',
    minWidth: 180,
    renderCell: params =>
      params.value === 'Unrestricted' ? (
        <Typography variant="body2">Unrestricted</Typography>
      ) : (
        <Box
          onMouseEnter={e => {
            setActiveRowId(params.row.carrierAuthorizationId);
            handlePopoverOpen(e);
          }}
          onMouseLeave={e => handlePopoverClose(e)}
          sx={{ cursor: 'pointer', display: 'inline-flex', py: 1 }}
        >
          <Typography sx={{ pointerEvents: 'none', textUnderlineOffset: '2px' }} variant="link">
            {params.value}
          </Typography>
        </Box>
      ),
    sortable: false,
  },
  ...(isProductLoadingManage
    ? [
        {
          field: 'actions',
          headerName: 'Actions',
          minWidth: 130,
          renderCell: params => (
            <ButtonGroup orientation="horizontal" size="small" variant="outlined">
              <Button
                disabled={!onEdit}
                onClick={() => onEdit?.(params.row.carrierAuthorizationId)}
              >
                Edit
              </Button>
              <MDropdownMenuButton variant="outlined">
                <MenuItem
                  onClick={() => {
                    const { carrierAuthorizationId, carrierName, location } = params.row;
                    onRemove({ carrierAuthorizationId, carrierName, location });
                  }}
                >
                  Remove
                </MenuItem>
              </MDropdownMenuButton>
            </ButtonGroup>
          ),
          sortable: false,
        },
      ]
    : []),
];

const mapApiRowsToGridRows = apiRows =>
  apiRows.map(item => ({
    carrierAuthorizationId: item.carrierAuthorizationId,
    carrierId: item.carrierId,
    carrierName: item.carrier || '--',
    customersRestricted: item.customersRestricted === 0 ? 'Unrestricted' : item.customersRestricted,
    fein: item.fein || '',
    id: item.carrierAuthorizationId,
    insuranceExpiration: item.insuranceExpiration,
    location: item.location || '--',
    nameShort: item.nameShort || '--',
    restrictedCustomerDetails: (item.restrictedCustomerDetails || [])
      .slice()
      .sort((a, b) => (a.customerName || '').localeCompare(b.customerName || '')),
    scac: item.scac || '--',
  }));

export default function CarrierAuthGrid({ onEdit, onExportingChange, appliedFilters = {} }) {
  const [persistedPageSize, setPersistedPageSize] = usePersistentState(
    'carrierAuthorizationsGridPageSize',
    50
  );
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: Number.isFinite(Number(persistedPageSize)) ? Number(persistedPageSize) : 50,
  });

  useEffect(() => {
    setPersistedPageSize(paginationModel.pageSize);
  }, [paginationModel.pageSize, setPersistedPageSize]);

  const [sortModel, setSortModel] = useState([{ field: 'carrierName', sort: 'asc' }]);
  const activeSort = sortModel[0] || {};
  const dispatch = useDispatch();
  const { isProductLoadingManage } = useUserRolePermissions();
  const { currentAffiliate } = useContext(AuthContext);

  const [removeCarrierAuthorization] = useRemoveCarrierAuthorizationMutation();
  const [removeBulkCarrierAuthorizations] = useRemoveBulkCarrierAuthorizationsMutation();
  const [triggerExport] = useLazyExportCarrierAuthorizationsQuery();
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [activeRowId, setActiveRowId] = useState(null);

  const { handlePopoverOpen, handlePopoverClose, forceClose, Popover } = useCustomizablePopover();

  const [isProcessing, setIsProcessing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    onExportingChange?.(isExporting);
  }, [isExporting, onExportingChange]);

  const prevFilterKeyRef = useRef(JSON.stringify(appliedFilters));

  useEffect(() => {
    const currentKey = JSON.stringify(appliedFilters);
    if (currentKey !== prevFilterKeyRef.current) {
      setRowSelectionModel([]);
      prevFilterKeyRef.current = currentKey;
    }
  }, [appliedFilters]);

  const carrierIds = useMemo(
    () => appliedFilters?.carriers?.map(c => c.carrierId) || [],
    [appliedFilters?.carriers]
  );

  const locationIds = useMemo(
    () => appliedFilters?.locations?.map(l => l.locationId) || [],
    [appliedFilters?.locations]
  );

  const queryParams = useMemo(() => {
    return {
      carrierIds,
      locationIds,
      ...paginationModel,
      sortColumn: SORT_COLUMN_MAP[activeSort.field],
      sortDirection: activeSort.sort === 'desc' ? 'descending' : 'ascending',
      ...(appliedFilters.affiliate?.customerIdName && {
        supplier: {
          companyCustomerIdName: appliedFilters.affiliate.customerIdName,
          dataCompanyId: appliedFilters.affiliate.dataCompanyId,
        },
      }),
      ...(appliedFilters.insuranceExpiration &&
        appliedFilters.insuranceExpiration !== 'All' && {
          insuranceExpirationFilter: appliedFilters.insuranceExpiration,
        }),
      ...(appliedFilters.search &&
        appliedFilters.search.trim() && { quickFilter: appliedFilters.search.trim() }),
    };
  }, [
    carrierIds,
    locationIds,
    paginationModel,
    activeSort.field,
    activeSort.sort,
    appliedFilters.affiliate?.customerIdName,
    appliedFilters.affiliate?.dataCompanyId,
    appliedFilters.insuranceExpiration,
    appliedFilters.search,
  ]);

  const {
    currentData: carrierAuthData = {},
    isFetching: loading,
    error: carrierAuthError,
  } = useGetCarrierAuthorizationsListQuery(
    { params: queryParams },
    {
      skip: !appliedFilters.affiliate?.customerIdName,
    }
  );

  const handleSingleRemoveClick = useCallback(
    ({ carrierAuthorizationId, carrierName, location }) => {
      dispatch(
        openConfirmationDialog({
          confirmAction: async () => {
            try {
              await removeCarrierAuthorization({
                carrierAuthorizationId,
                supplier: currentAffiliate,
              }).unwrap();

              setRowSelectionModel(prev => prev.filter(rowId => rowId !== carrierAuthorizationId));

              dispatch(
                createSnack({
                  message: 'Authorization has been successfully removed.',
                  options: { variant: 'success' },
                  title: 'Action Completed',
                })
              );
            } catch (error) {
              dispatch(
                createSnack({
                  message:
                    error?.data?.detail ||
                    'Due to an error, the authorization could not be removed.',
                  options: { variant: 'error' },
                  title: 'Action Not Completed',
                })
              );
            }
          },
          header: 'Remove Carrier Authorization',
          message: `Are you sure you want to remove the authorization for ${carrierName} at ${location}?`,
          params: {},
        })
      );
    },
    [dispatch, removeCarrierAuthorization, currentAffiliate]
  );

  const handleBulkRemoveClick = useCallback(() => {
    const selectedIds = [...rowSelectionModel];
    const count = selectedIds.length;

    dispatch(
      openConfirmationDialog({
        confirmAction: async () => {
          try {
            setIsProcessing(true);
            await removeBulkCarrierAuthorizations({
              carrierAuthorizationIds: selectedIds,
            }).unwrap();

            setRowSelectionModel([]);

            setIsProcessing(false);

            dispatch(
              createSnack({
                message: 'Authorizations have been successfully removed.',
                options: { variant: 'success' },
                title: 'Action Completed',
              })
            );
          } catch (error) {
            dispatch(
              createSnack({
                message:
                  error?.data?.detail ||
                  'Due to an error, the authorizations could not be removed.',
                options: { variant: 'error' },
                title: 'Action Not Completed',
              })
            );
          } finally {
            setIsProcessing(false);
          }
        },
        header: `Remove ${count} Authorization${count !== 1 ? 's' : ''}`,
        message: 'Are you sure you want to remove the selected authorizations?',
        params: {},
      })
    );
  }, [dispatch, rowSelectionModel, removeBulkCarrierAuthorizations]);

  useEffect(() => {
    if (!carrierAuthError) return;

    // Ignore abort errors caused by superseded requests
    const isAbortError =
      carrierAuthError?.status === 'FETCH_ERROR' &&
      String(carrierAuthError?.error || '').includes('AbortError');
    if (isAbortError) return;

    const errorMessage =
      carrierAuthError?.data?.title ||
      carrierAuthError?.error ||
      'Due to an error, your carrier authorizations could not be retrieved. Please reload the page to try again.';
    dispatch(
      createSnack({
        message: errorMessage,
        options: { variant: 'error' },
        title: 'Action Not Completed',
      })
    );
  }, [dispatch, carrierAuthError]);

  const rows = useMemo(
    () => carrierAuthData.carrierAuthorizations || [],
    [carrierAuthData.carrierAuthorizations]
  );
  const rowCount = carrierAuthData.totalResults || 0;
  const authorizationCount = loading ? 0 : rowCount;
  const mappedRows = useMemo(() => mapApiRowsToGridRows(rows), [rows]);
  const currentPageRowIds = useMemo(() => mappedRows.map(row => row.id), [mappedRows]);

  const handleRowSelectionModelChange = useCallback(
    newSelectionModel => {
      const currentPageIdSet = new Set(currentPageRowIds);
      const otherPageSelections = rowSelectionModel.filter(id => !currentPageIdSet.has(id));
      const currentPageSelections = newSelectionModel.filter(id => currentPageIdSet.has(id));
      setRowSelectionModel([...otherPageSelections, ...currentPageSelections]);
    },
    [rowSelectionModel, currentPageRowIds]
  );

  const selectedCount = rowSelectionModel.length;
  const hasSelections = selectedCount > 0;

  const paginationStatusDisplay = useMemo(() => {
    const totalDisplay = formatNumber(authorizationCount, { defaultValue: 0 });
    if (hasSelections) {
      return `${formatNumber(selectedCount, {
        defaultValue: 0,
      })}  of ${totalDisplay} Authorizations`;
    }
    return `${totalDisplay} Authorizations`;
  }, [authorizationCount, selectedCount, hasSelections]);

  const handleSortModelChange = newSortModel => {
    const nextSortModel =
      newSortModel && newSortModel.length ? newSortModel : [{ field: 'carrierName', sort: 'asc' }];
    setSortModel(nextSortModel);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const memoizedColumns = useMemo(
    () =>
      columns(onEdit, handleSingleRemoveClick, isProductLoadingManage, {
        handlePopoverClose,
        handlePopoverOpen,
        setActiveRowId,
      }),
    [onEdit, handleSingleRemoveClick, isProductLoadingManage, handlePopoverClose, handlePopoverOpen]
  );

  const CustomActionFooter = useCallback(() => {
    return (
      <BulkActionFooter
        actions={[
          <MenuItem
            onClick={() => {
              handleBulkRemoveClick();
            }}
          >
            Remove selected
          </MenuItem>,
        ]}
        buttonSpacing="flex-end"
        disabled={selectedCount === 0 || isProcessing}
        primaryActionButtonTitle="Bulk Actions"
      />
    );
  }, [handleBulkRemoveClick, selectedCount, isProcessing]);

  const currentPageSelection = useMemo(
    () => rowSelectionModel.filter(id => currentPageRowIds.includes(id)),
    [rowSelectionModel, currentPageRowIds]
  );

  const activeRow = mappedRows.find(row => row.id === activeRowId);
  const popoverCustomers = activeRow?.restrictedCustomerDetails || [];
  const isRestrictedButNoDetails =
    activeRow?.customersRestricted !== 0 && popoverCustomers.length === 0;
  const visibleCustomers = popoverCustomers.slice(0, MAX_VISIBLE_CUSTOMERS);
  const remainingCount = popoverCustomers.length - MAX_VISIBLE_CUSTOMERS;

  return (
    <>
      <PersistentServerSideDataGrid
        bulkActionFooter={isProductLoadingManage ? CustomActionFooter : null}
        checkboxSelection={isProductLoadingManage}
        columns={memoizedColumns}
        defaultPageSize={paginationModel.pageSize}
        disableColumnMenu
        disableColumnReorder={isExporting}
        disabledExport={authorizationCount === 0}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        id="carrierAuthorizationsGrid"
        initialState={{
          pinnedColumns: {
            right: ['actions'],
          },
        }}
        isExporting={isExporting}
        isProcessing={isProcessing}
        keepNonExistentRowsSelected
        loading={loading || isProcessing || isExporting}
        onExportAllRows={async () => {
          setIsExporting(true);
          try {
            const { page, pageSize, ...exportQueryParams } = queryParams;

            const result = await triggerExport(exportQueryParams, false);

            if (result?.error) {
              throw result.error;
            }
            const csvBlob =
              result?.data instanceof Blob
                ? result.data
                : new Blob([result?.data || ''], { type: 'text/csv;charset=utf-8;' });
            const filename = 'carrier-authorizations.csv';

            const url = URL.createObjectURL(csvBlob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = filename;
            anchor.click();
            URL.revokeObjectURL(url);

            dispatch(
              createSnack({
                message: 'Export has been successfully downloaded.',
                options: { variant: 'success' },
                title: 'Action Completed',
              })
            );
          } catch (error) {
            const errorMessage =
              error?.data?.detail ||
              error?.data?.title ||
              error?.error ||
              'Due to an error, your carrier authorizations could not be exported to a .CSV file.';

            dispatch(
              createSnack({
                message: errorMessage,
                options: { persist: true, variant: 'error' },
                title: 'Action Not Completed',
              })
            );
            throw error;
          } finally {
            setIsExporting(false);
          }
        }}
        onPaginationChange={setPaginationModel}
        onResetView={() => setRowSelectionModel([])}
        onRowSelectionModelChange={
          isProductLoadingManage ? handleRowSelectionModelChange : undefined
        }
        onSortChange={handleSortModelChange}
        paginationStatus={paginationStatusDisplay}
        rows={mappedRows}
        rowSelectionModel={isProductLoadingManage ? currentPageSelection : undefined}
        totalRows={authorizationCount}
      />
      <Popover
        id="restricted-customers-popover"
        popoverFooterContent={
          remainingCount > 0 && (
            <Typography
              onClick={() => {
                forceClose();
                onEdit?.(activeRowId);
              }}
              sx={{ cursor: 'pointer', textUnderlineOffset: '2px' }}
              variant="link"
            >
              + {remainingCount} More
            </Typography>
          )
        }
        popoverHeaderContent={<Typography variant="body1bold">Restricted Customers</Typography>}
        popoverMainContent={
          isRestrictedButNoDetails ? (
            <Typography color="error" variant="body2">
              Unable to retrieve restricted customers
            </Typography>
          ) : (
            <Stack spacing={1}>
              {visibleCustomers.map(customer => (
                <Box
                  key={`restricted-${customer.customerIdName}`}
                  sx={{
                    alignItems: 'baseline',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography
                    sx={{
                      color: 'text.primary',
                      maxWidth: 150,
                      mr: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    variant="body1"
                  >
                    {customer.customerName}
                  </Typography>
                  <Typography sx={{ color: 'text.primary' }} variant="subtitle2">
                    {customer.customerIdName}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )
        }
      />
    </>
  );
}

CarrierAuthGrid.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  appliedFilters: PropTypes.object,
  onEdit: PropTypes.func,
  onExportingChange: PropTypes.func,
};

CarrierAuthGrid.defaultProps = {
  appliedFilters: {},
  onEdit: null,
  onExportingChange: null,
};
